import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import { Sparkles, Plus, Loader2, Trash2, ChevronRight, ChevronLeft, FileText, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: { label: "Sıraya alındı", color: "ky-badge-neutral", icon: Clock },
  generating: { label: "Üretiliyor", color: "ky-badge-warning", icon: Loader2 },
  done: { label: "Tamamlandı", color: "ky-badge-success", icon: CheckCircle2 },
  failed: { label: "Başarısız", color: "ky-badge-danger", icon: AlertCircle },
};

export default function AdCopyToolPage() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await apiClient.get("/ai-tools/ad-copy/jobs"); setJobs(data || []); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const remove = async (id) => {
    if (!window.confirm("Sil?")) return;
    try { await apiClient.delete(`/ai-tools/ad-copy/jobs/${id}`); load(); toast.success("Silindi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="ad-copy-tool">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/ai-araclar" className="hover:text-[#4F46E5] flex items-center gap-1"><ChevronLeft size={14}/>AI Araçlar</Link>
        <span>/</span>
        <span className="font-medium text-[#0F172A]">Reklam Metni Üretici</span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#FEF3C7] flex items-center justify-center shrink-0"><Sparkles size={20} className="text-[#D97706]"/></div>
          <div>
            <h1 className="text-2xl font-bold">Reklam Metni Üretici</h1>
            <p className="text-sm text-[#64748B] mt-1 max-w-2xl">Google Ads RSA için 15 başlık (≤30 kar) ve 4 açıklama (≤90 kar) üretir. Anahtar kelimeleri, tonu ve marka bilgisini sen belirlersin.</p>
          </div>
        </div>
        <button onClick={()=>nav("/ai-araclar/reklam-metni/yeni")} className="ky-btn ky-btn-accent" data-testid="new-ad-copy"><Plus size={14}/>Yeni Reklam Metni</button>
      </div>

      <div className="ky-card p-0 overflow-hidden">
        {loading && <div className="p-10 text-center text-sm text-[#94A3B8]"><Loader2 size={14} className="animate-spin inline mr-2"/>Yükleniyor…</div>}
        {!loading && jobs.length === 0 && (
          <div className="p-12 text-center">
            <Sparkles size={32} className="mx-auto text-[#CBD5E1] mb-3"/>
            <div className="text-sm font-medium text-[#0F172A]">Henüz reklam metni yok</div>
            <p className="text-xs text-[#94A3B8] mt-1">İlk üretimi başlatmak için "Yeni" butonuna tıkla.</p>
          </div>
        )}
        {!loading && jobs.map(j => {
          const st = STATUS[j.status] || STATUS.pending; const Ic = st.icon;
          const inProgress = j.status === "pending" || j.status === "generating";
          return (
            <div key={j.id} className="px-5 py-3 border-b border-[#ECECEF] last:border-b-0 hover:bg-[#FAFAFB] flex items-center gap-3" data-testid={`ad-job-${j.id}`}>
              <div onClick={()=>nav(`/ai-araclar/reklam-metni/${j.id}`)} className="flex-1 min-w-0 cursor-pointer">
                <div className="flex items-center gap-2 mb-0.5">
                  <FileText size={13} className="text-[#D97706]"/>
                  <span className="font-medium text-sm">{j.tone || "Profesyonel"} ton</span>
                  <span className={`ky-badge ${st.color} text-[10px]`}><Ic size={10} className={inProgress?"animate-spin":""}/>{st.label}</span>
                </div>
                <div className="text-[11px] text-[#94A3B8] truncate">
                  {j.customer ? `${j.customer.name} · ` : ""}
                  {(j.keywords||[]).length} kelime
                  {j.result?.headlines ? ` · ${j.result.headlines.length} başlık · ${j.result.descriptions.length} açıklama` : ""}
                  {j.cost?.usd ? ` · $${j.cost.usd.toFixed(4)}` : ""}
                  {" · "}{new Date(j.created_at).toLocaleString("tr-TR")}
                </div>
                {inProgress && (
                  <div className="mt-1.5 h-1 bg-[#F4F4F5] rounded-full overflow-hidden">
                    <div className="h-full bg-[#D97706] transition-all" style={{ width: `${j.progress || 0}%` }}/>
                  </div>
                )}
              </div>
              <button onClick={()=>nav(`/ai-araclar/reklam-metni/${j.id}`)} className="ky-btn ky-btn-ghost ky-btn-icon"><ChevronRight size={14}/></button>
              <button onClick={()=>remove(j.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]"><Trash2 size={13}/></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
